import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Share2, Loader2, Download, Copy, X, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * ShareClipButton — one-tap "turn this reply into a shareable 15s MP4".
 *
 * Clicking kicks off a backend render job, polls until the clip is ready,
 * then opens a modal with a <video> preview + download link + copy-link
 * button. Preserves the companion's voice (ElevenLabs preferred) and uses
 * the chapter art if supplied, else the companion's portrait.
 *
 * Mythos subscribers see a "Remove wordmark" toggle for cleaner personal
 * exports; free users see the toggle locked with an upsell tooltip.
 */
export default function ShareClipButton({ companionId, text, coverUrl, className = "" }) {
  const { user } = useAuth();
  const isSubscriber = !!user && user.subscription_status && user.subscription_status !== "free";
  const [state, setState] = useState("idle");  // idle | rendering | ready | error
  const [clip, setClip] = useState(null);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stripWatermark, setStripWatermark] = useState(false);
  const [captions, setCaptions] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const start = async () => {
    if (state === "rendering") return;
    if (stripWatermark && !isSubscriber) {
      toast.error("Removing the wordmark is a Mythos perk — upgrade or keep it on.");
      return;
    }
    setState("rendering");
    setProgress(5);
    setClip(null);
    setOpen(true);
    try {
      const { data } = await api.post("/share/companion-clip", {
        companion_id: companionId,
        text,
        cover_url: coverUrl || null,
        include_watermark: !stripWatermark,
        enable_captions: captions,
      });
      const clipId = data.clip_id;
      // Poll every 2s. Share clips typically render in ~15-25s.
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/share/companion-clip/${clipId}`);
          setClip(r.data);
          setProgress(r.data.progress || progress);
          if (r.data.status === "ready") {
            clearInterval(pollRef.current);
            setState("ready");
            setProgress(100);
          } else if (r.data.status === "failed") {
            clearInterval(pollRef.current);
            setState("error");
            toast.error(r.data.error || "Clip render failed");
          }
        } catch (e) {
          clearInterval(pollRef.current);
          setState("error");
          toast.error(e.response?.data?.detail || "Clip render failed");
        }
      }, 2000);
    } catch (e) {
      setState("error");
      toast.error(e.response?.data?.detail || "Could not start render");
    }
  };

  const close = () => {
    setOpen(false);
    if (state !== "rendering") {
      setState("idle");
      setClip(null);
      setProgress(0);
    }
  };

  const copyLink = async () => {
    if (!clip?.download_url) return;
    const absolute = window.location.origin + clip.download_url;
    try {
      await navigator.clipboard.writeText(absolute);
      toast.success("Clip link copied — ready for TikTok / Reels");
    } catch {
      toast.error("Could not copy — long-press the player to save instead");
    }
  };

  return (
    <>
      <button
        data-testid="share-clip-button"
        onClick={() => { setOpen(true); setState("idle"); }}
        disabled={state === "rendering"}
        title="Turn this reply into a 15-second TikTok clip"
        className={`inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 hover:border-amber-400/50 hover:text-amber-200 text-slate-400 text-[11px] font-mono uppercase tracking-widest h-7 px-2.5 ${className}`}
      >
        {state === "rendering" ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" /> Rendering…
          </>
        ) : (
          <>
            <Share2 className="h-3 w-3" /> Share clip
          </>
        )}
      </button>
      {open && (
        <div
          data-testid="share-clip-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          onClick={close}
        >
          <div
            className="relative max-w-md w-full rounded-3xl border border-white/10 bg-[#0F1117] shadow-[0_12px_60px_rgba(0,0,0,0.8)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              aria-label="Close"
              onClick={close}
              className="absolute top-3 right-3 z-10 rounded-full p-1.5 bg-black/50 border border-white/10 text-slate-400 hover:text-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
            {state === "idle" && (
              <div className="p-6 space-y-5">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-1 flex items-center gap-1.5"><Share2 className="h-3 w-3" /> Export options</div>
                  <h3 className="font-serif text-xl text-slate-100">Make it a clip</h3>
                  <p className="mt-1 text-xs text-slate-400">15 seconds · 1080×1080 · perfect for TikTok, Reels and Shorts.</p>
                </div>
                <label className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-black/30 cursor-pointer hover:border-amber-400/30" data-testid="toggle-captions">
                  <input
                    type="checkbox"
                    checked={captions}
                    onChange={(e) => setCaptions(e.target.checked)}
                    className="mt-0.5 accent-amber-400"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-100">TikTok-style captions</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Words pop in sync with the voice. Great for silent-scroll.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-xl border bg-black/30 cursor-pointer transition-colors ${isSubscriber ? "border-white/10 hover:border-amber-400/30" : "border-amber-400/20 opacity-70"}`} data-testid="toggle-watermark">
                  <input
                    type="checkbox"
                    checked={stripWatermark && isSubscriber}
                    disabled={!isSubscriber}
                    onChange={(e) => setStripWatermark(e.target.checked)}
                    className="mt-0.5 accent-amber-400"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-100 flex items-center gap-1.5">
                      Remove ProseCraft wordmark
                      {!isSubscriber && <Crown className="h-3 w-3 text-amber-400" />}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {isSubscriber
                        ? "Clean export for your own channel. Mythos perk."
                        : <>Unlock with a <a href="/pricing" className="text-amber-300 underline hover:text-amber-200">Mythos membership</a>.</>}
                    </div>
                  </div>
                </label>
                <button
                  data-testid="share-clip-render"
                  onClick={start}
                  className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11"
                >
                  Render clip
                </button>
              </div>
            )}
            {state === "idle" ? null : state === "rendering" && (
              <div className="aspect-square flex flex-col items-center justify-center p-6 text-center">
                <Loader2 className="h-10 w-10 text-amber-400 animate-spin mb-4" />
                <div className="font-serif text-lg text-slate-100">Rendering your clip…</div>
                <div className="mt-3 w-full max-w-xs h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-amber-400 transition-all" style={{ width: `${Math.min(progress, 95)}%` }} />
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Narration · Ken-Burns · {captions ? "captions · " : ""}{stripWatermark ? "no watermark" : "wordmark"}
                </div>
              </div>
            )}
            {state === "ready" && clip?.download_url && (
              <>
                <video
                  data-testid="share-clip-video"
                  src={fileUrl(clip.download_url)}
                  controls
                  autoPlay
                  playsInline
                  loop
                  className="w-full aspect-square bg-black"
                />
                <div className="p-5">
                  <div className="font-serif text-lg text-slate-100">Clip ready</div>
                  <div className="mt-1 text-xs text-slate-400">
                    1080×1080 · 15s · perfect for TikTok, Reels and Shorts.
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <a
                      data-testid="share-clip-download"
                      href={fileUrl(clip.download_url)}
                      download={`${(clip.companion_name || "clip").replace(/[^a-z0-9]/gi, "_")}.mp4`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 text-sm font-semibold"
                    >
                      <Download className="h-4 w-4" /> Download MP4
                    </a>
                    <button
                      data-testid="share-clip-copy"
                      onClick={copyLink}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 hover:border-amber-400/40 text-slate-200 px-4 py-2 text-sm"
                    >
                      <Copy className="h-4 w-4" /> Copy link
                    </button>
                  </div>
                </div>
              </>
            )}
            {state === "error" && (
              <div className="aspect-square flex flex-col items-center justify-center p-6 text-center">
                <div className="text-rose-300 text-sm">Clip render failed — try again.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
